import { FC, useState, useEffect, useMemo } from "react";
import {
  Heading,
  Flex,
  Box,
  Text,
  Icon,
  Modal,
  ModalContent,
  ModalCloseButton,
  ModalOverlay,
  Button,
} from "@chakra-ui/react";
import { IoIosRadioButtonOff, IoIosRadioButtonOn } from "react-icons/io";

import { Service } from "./Service";
import {
  parseDynamicPrices,
  getExtraLabel,
  getOrderedOptionIds,
  getBaseOptionId,
  getPriceStep,
} from "../core/dynamicPricing";

interface ServiceListProps {
  service: any;
  modalDisclosure: any;
  drawerDisclosure: any;
  business_id: string;
  extras: any[];
  cart: any[];
  setCart: any;
}

export const ServiceList: FC<ServiceListProps> = ({
  service,
  modalDisclosure,
  drawerDisclosure,
  business_id,
  extras,
  cart,
  setCart,
}) => {
  const [dynamicPricing, setDynamicPricing] = useState<string>("");
  const [selectedLength, setSelectedLength] = useState<string>("");
  const [selectedSize, setSelectedSize] = useState<string>("");

  const [selectedService, setSelectedService] = useState<any>();

  const closeModal = () => {
    setDynamicPricing("");
    modalDisclosure.onClose();
  };

  const selectedServiceDynamicPricing = useMemo(
    () => parseDynamicPrices(selectedService?.dynamic_prices),
    [selectedService],
  );

  // Display order, top to bottom, taken from the extras catalog.
  const orderedLengthIds = useMemo(
    () =>
      getOrderedOptionIds(
        extras,
        "length",
        selectedServiceDynamicPricing?.length?.values,
      ),
    [extras, selectedServiceDynamicPricing],
  );

  const orderedSizeIds = useMemo(
    () =>
      getOrderedOptionIds(
        extras,
        "size",
        selectedServiceDynamicPricing?.size?.values,
      ),
    [extras, selectedServiceDynamicPricing],
  );

  // Steps below each group's base option. The base option is free, so the
  // topmost length and the topmost size both leave the price untouched.
  const lengthStep = getPriceStep(orderedLengthIds, selectedLength);
  const sizeStep = getPriceStep(orderedSizeIds, selectedSize);

  const finalPrice =
    Number(selectedService?.price || 0) +
    Number(selectedServiceDynamicPricing?.length?.additional_price || 0) *
      lengthStep +
    Number(selectedServiceDynamicPricing?.size?.additional_price || 0) *
      sizeStep;

  const finalDuration =
    Number(selectedService?.duration_minute || 0) +
    Number(selectedServiceDynamicPricing?.length?.additional_duration || 0) *
      lengthStep +
    Number(selectedServiceDynamicPricing?.size?.additional_duration || 0) *
      sizeStep;

  const handleSubmit = () => {
    const isDynamicPriceAvailable =
      !!selectedServiceDynamicPricing?.size?.additional_price;

    if (dynamicPricing === "length" && isDynamicPriceAvailable)
      setDynamicPricing("size");
    else {
      setDynamicPricing("");
      setCart((prev: any) => [
        ...prev,
        {
          service: selectedService,
          selectedLength,
          selectedSize,
          business_id,
          finalPrice,
          finalDuration,
        },
      ]);

      setSelectedLength("");
      setSelectedSize("");

      modalDisclosure.onClose();
    }
  };

  // Both default to their group's base option, which adds nothing to the price.
  useEffect(() => {
    setSelectedLength(getBaseOptionId(orderedLengthIds));
    setSelectedSize(getBaseOptionId(orderedSizeIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService, orderedLengthIds, orderedSizeIds]);

  return (
    <>
      {service.services.map((_service: any) => (
        <Service
          key={_service._id}
          service={_service}
          modalDisclosure={modalDisclosure}
          setSelectedService={setSelectedService}
          setDynamicPricing={setDynamicPricing}
          setCart={setCart}
          selectedLength={selectedLength}
          selectedSize={selectedSize}
          business_id={business_id}
        />
      ))}

      {cart.length > 0 && (
        <Button
          bg={"#FF8E3C"}
          _hover={{ bg: "#FF8E3C" }}
          borderRadius={"30px"}
          color={"white"}
          w="100%"
          h="58px"
          mt="10px"
          fontSize={"17px"}
          fontFamily={"RedHatDisplay-Bold"}
          onClick={() => drawerDisclosure.onOpen()}
          display={{ base: "block", md: "none" }}
          rightIcon={
            <Flex
              w={"30px"}
              h={"30px"}
              bg="#19202D"
              borderRadius={"50%"}
              align={"center"}
              justify={"center"}
            >
              <Text color="#FFF" fontSize={"17px"} fontWeight={700}>
                {cart.length}
              </Text>
            </Flex>
          }
        >
          Checkout
        </Button>
      )}

      <Modal
        isCentered
        isOpen={modalDisclosure.isOpen}
        onClose={closeModal}
        size={"xs"}
      >
        <ModalOverlay bg="#999" backdropFilter="blur(10px) hue-rotate(90deg)" />

        <ModalContent
          borderRadius={"20px"}
          pt="20px"
          px="24px"
          bg={"linear-gradient(#FFFFFF, #F6F6F7)"}
        >
          <Text fontSize={"16px"} color="#4F4F51" fontWeight={400}>
            Please Select ({dynamicPricing === "length" ? 1 : 2}/2)
          </Text>

          <Text fontSize={"16px"} fontWeight={700} color={"#19202D"}>
            {selectedService?.name}
          </Text>

          <ModalCloseButton />

          <Box mt="16px">
            <Text
              fontSize={"16px"}
              fontWeight={700}
              color={"#19202D"}
              mb="8px"
              textTransform={"capitalize"}
            >
              {dynamicPricing}
            </Text>

            {selectedService &&
              (dynamicPricing === "length"
                ? orderedLengthIds
                : orderedSizeIds
              ).map((option: string) => {
                let optionIcon;
                if (dynamicPricing === "length") {
                  if (selectedLength === option)
                    optionIcon = <IoIosRadioButtonOn />;
                  else optionIcon = <IoIosRadioButtonOff />;
                } else if (dynamicPricing === "size") {
                  if (selectedSize === option)
                    optionIcon = <IoIosRadioButtonOn />;
                  else optionIcon = <IoIosRadioButtonOff />;
                } else optionIcon = <IoIosRadioButtonOff />;

                return (
                  <Flex
                    gap="8px"
                    mt="12px"
                    key={option}
                    onClick={() =>
                      dynamicPricing === "length"
                        ? setSelectedLength(option)
                        : setSelectedSize(option)
                    }
                    cursor={"pointer"}
                  >
                    <Icon fontSize={"24px"} color={"#FF8E3C"}>
                      {optionIcon}
                    </Icon>

                    <Text fontSize={"16px"} color={"#4F4F51"}>
                      {getExtraLabel(extras, dynamicPricing, option)}
                    </Text>
                  </Flex>
                );
              })}
          </Box>

          <Box
            borderBottomLeftRadius={"20px"}
            borderBottomRightRadius={"20px"}
            mt="20px"
            py="20px"
            px="24px"
            bgImage={"url('../../assets/images/spikedBg.png')"}
            bgPosition={"center"}
            bgRepeat={"no-repeat"}
            mx="-24px"
            // h={"100px"}
            bg="#FFFFFF"
          >
            <Flex align={"center"} justify={"space-between"} mb="16px">
              <Heading
                fontSize={"16px"}
                color="#4F4F51"
                fontFamily={"RedHatDisplay-Bold"}
              >
                PRICE
              </Heading>

              <Text fontSize={"16px"} color={"#267684"} fontWeight={600}>
                £ {finalPrice}
              </Text>
            </Flex>

            <Button
              bg={"#FF8E3C"}
              _hover={{ bg: "#FF8E3C" }}
              borderRadius={"30px"}
              color={"white"}
              w="100%"
              h="58px"
              fontSize={"17px"}
              fontFamily={"RedHatDisplay-Bold"}
              onClick={handleSubmit}
            >
              Continue
            </Button>
          </Box>
        </ModalContent>
      </Modal>
    </>
  );
};
